import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Send, Paperclip, Loader2, FileText, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { sendEmail } from "@/lib/api";
import { formatBytes } from "@/lib/constants";

export const EmailModal = ({ open, onOpenChange, client, onSent }) => {
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (client) {
      setRecipient(client.email || "");
      setSubject(`Voucher y Documentación de Viaje — ${client.hotel || "ViajaCRM"}`);
      const travelDate = client.reservation?.travel_date
        ? `con fecha de salida programada para el ${client.reservation.travel_date}`
        : "";
      setMessage(
        `Estimado(a) ${client.full_name},\n\nNos complace compartirte la confirmación y documentación oficial de tu reserva en ${
          client.hotel || "tu destino"
        } ${travelDate}.\n\nPor favor revisa los documentos adjuntos y no dudes en contactarnos ante cualquier duda.\n\n¡Te deseamos un excelente viaje!\nAtentamente,\nEquipo de ViajaCRM`
      );
      // Pre-select all documents by default
      setSelectedDocs((client.documents || []).map((d) => d.id));
    }
  }, [client]);

  const toggleDoc = (id) => {
    setSelectedDocs((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!recipient.trim()) {
      toast.error("Por favor ingresa un correo de destinatario");
      return;
    }
    if (!subject.trim()) {
      toast.error("Por favor ingresa un asunto");
      return;
    }

    setLoading(true);
    try {
      const res = await sendEmail(client.id, {
        recipient: recipient.trim(),
        subject: subject.trim(),
        message: message.trim(),
        document_ids: selectedDocs,
      });

      toast.success(res.message || "Correo enviado exitosamente");
      onSent?.();
      onOpenChange(false);
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al enviar el correo";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2 text-slate-800">
            <Mail className="h-5 w-5 text-slate-600" />
            <DialogTitle className="font-heading text-lg">
              Enviar Documentos por Correo
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-slate-500">
            Envía vouchers, itinerarios y confirmaciones a {client.full_name} ({client.client_code}).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSend} className="p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Destinatario</label>
            <Input
              type="email"
              placeholder="cliente@ejemplo.com"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              className="h-9 bg-white"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Asunto</label>
            <Input
              type="text"
              placeholder="Asunto del correo"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="h-9 bg-white"
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700">Mensaje</label>
            <Textarea
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="bg-white text-xs leading-relaxed"
              required
            />
          </div>

          {/* Attachments selection */}
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <Paperclip className="h-3.5 w-3.5 text-slate-400" /> Adjuntar Documentos del Cliente
              </label>
              <span className="text-xs text-slate-400">
                {selectedDocs.length} de {(client.documents || []).length} seleccionados
              </span>
            </div>

            {(client.documents || []).length === 0 ? (
              <p className="text-xs text-slate-400 italic bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                Este cliente no tiene documentos subidos todavía.
              </p>
            ) : (
              <div className="max-h-36 overflow-y-auto space-y-1.5 p-2 bg-slate-50 rounded-lg border border-slate-200">
                {(client.documents || []).map((doc) => {
                  const checked = selectedDocs.includes(doc.id);
                  return (
                    <button
                      type="button"
                      key={doc.id}
                      onClick={() => toggleDoc(doc.id)}
                      className={`w-full flex items-center justify-between p-2 rounded-md border text-left text-xs transition-colors ${
                        checked
                          ? "bg-white border-slate-300 text-slate-800 shadow-xs"
                          : "bg-transparent border-transparent text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      <div className="flex items-center gap-2 truncate pr-2">
                        <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span className="truncate font-medium">{doc.filename}</span>
                        <span className="text-[10px] text-slate-400 shrink-0">
                          ({formatBytes(doc.size)})
                        </span>
                      </div>
                      <div
                        className={`h-4 w-4 rounded flex items-center justify-center shrink-0 border ${
                          checked
                            ? "bg-slate-900 border-slate-900 text-white"
                            : "border-slate-300 bg-white"
                        }`}
                      >
                        {checked && <Check className="h-3 w-3 stroke-[3]" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
            >
              {loading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5" />
                  <span>Enviar Correo</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
